import { createAsyncThunk, createSlice, createAction } from '@reduxjs/toolkit';
import serviceEndpoints from '../../../config/serviceEndpoints';
import proposalGateway from '../../../config/service';
import { getQueryParams, Notifications } from '../../../utils';

// ─── Reset actions ────────────────────────────────────────────────────────────
export const resetVideoMessages = createAction('videoManagement/resetMessages');
export const resetVideoDetail = createAction('videoManagement/resetDetail');

// ─── 1. List Videos ───────────────────────────────────────────────────────────
export const getVideoList = createAsyncThunk('videoManagement/list', async (data, { rejectWithValue }) => {
    try {
        const response = await proposalGateway.get(`${serviceEndpoints.videoManagement}?${getQueryParams(data)}`);
        const { success, message } = response.data;
        if (success) return response.data;
        return rejectWithValue(message);
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

// ─── 2. Get Avatars ───────────────────────────────────────────────────────────
export const getAvatarList = createAsyncThunk('videoManagement/avatars', async (_, { rejectWithValue }) => {
    try {
        const response = await proposalGateway.get(`${serviceEndpoints.videoManagement}/avatars`);
        const { success, message } = response.data;
        if (success) return response.data;
        return rejectWithValue(message);
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

// ─── 3. Get Voices ────────────────────────────────────────────────────────────
export const getVoiceList = createAsyncThunk('videoManagement/voices', async (_, { rejectWithValue }) => {
    try {
        const response = await proposalGateway.get(`${serviceEndpoints.videoManagement}/voices`);
        const { success, message } = response.data;
        if (success) return response.data;
        return rejectWithValue(message);
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

// ─── 4. Generate Video ────────────────────────────────────────────────────────
export const generateVideo = createAsyncThunk('videoManagement/generate', async (data, { rejectWithValue }) => {
    try {
        // Strip the client-side idempotency key from the body and forward it as a
        // standard header so the backend can deduplicate retries without changing
        // the API body contract.
        const { _idempotencyKey, ...payload } = data;
        const config = _idempotencyKey ? { headers: { 'X-Idempotency-Key': _idempotencyKey } } : {};
        const response = await proposalGateway.post(
            `${serviceEndpoints.videoManagement}/generate-video`,
            payload,
            config
        );
        const { success, message } = response.data;
        if (success) {
            Notifications(message || 'Video generation started!', 'warning');
            return response.data;
        }
        return rejectWithValue(message);
    } catch (error) {
        const msg = error.response?.data?.message || error.response?.data?.error || error.message;
        return rejectWithValue(msg); // ← just pass the raw msg, NO Notifications here
    }
});
// ─── 5. Get Video Status ──────────────────────────────────────────────────────
export const getVideoStatus = createAsyncThunk('videoManagement/status', async (heygenVideoId, { rejectWithValue }) => {
    try {
        const response = await proposalGateway.get(`${serviceEndpoints.videoManagement}/video-status/${heygenVideoId}`);
        const { success, message } = response.data;
        if (success) return response.data;
        return rejectWithValue(message);
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

// ─── 6. Get Video By ID ───────────────────────────────────────────────────────
export const getVideoById = createAsyncThunk('videoManagement/getById', async (id, { rejectWithValue }) => {
    try {
        const response = await proposalGateway.get(`${serviceEndpoints.videoManagement}/${id}`);
        const { success, message } = response.data;
        if (success) return response.data;
        return rejectWithValue(message);
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

// ─── 7. Edit Video ────────────────────────────────────────────────────────────
export const editVideo = createAsyncThunk('videoManagement/edit', async ({ id, ...data }, { rejectWithValue }) => {
    try {
        const response = await proposalGateway.put(`${serviceEndpoints.videoManagement}/${id}`, data);
        const { success, message } = response.data;
        if (success) {
            Notifications(message || 'Video updated successfully!', 'success');
            return response.data;
        }
        return rejectWithValue(message);
    } catch (error) {
        if (error.response?.status === 400 || error.response?.status === 401) {
            return rejectWithValue(error.response.data);
        }
        return rejectWithValue(error.message);
    }
});

// ─── 8. Delete Video ──────────────────────────────────────────────────────────
export const deleteVideo = createAsyncThunk('videoManagement/delete', async ({ id, cb }, { rejectWithValue }) => {
    try {
        const response = await proposalGateway.delete(`${serviceEndpoints.videoManagement}/${id}`);
        const { success, message } = response.data;
        if (success) {
            Notifications(message || 'Video deleted successfully!', 'success');
            if (cb) cb();
            return response.data;
        }
        return rejectWithValue(message);
    } catch (error) {
        if (error.response?.status === 400 || error.response?.status === 401) {
            return rejectWithValue(error.response.data);
        }
        return rejectWithValue(error.message);
    }
});

// ─── 9. Download Video ────────────────────────────────────────────────────────
export const downloadVideo = createAsyncThunk('video-management/download-video', async (id, { rejectWithValue }) => {
    try {
        const response = await proposalGateway.get(`${serviceEndpoints.videoManagement}/download-video/${id}`, {
            timeout: 90000,
        });
        if (response.status < 200 || response.status >= 300) {
            return rejectWithValue(response.data);
        }
        return response.data;
    } catch (error) {
        if (error.response?.status === 400 || error.response?.status === 401) {
            return rejectWithValue(error.response.data);
        }
        return rejectWithValue(error.message);
    }
});

// ─── 10. Refresh Video Status ─────────────────────────────────────────────────
export const refreshVideoStatus = createAsyncThunk(
    'video-management/refreshStatus',
    async (id, { rejectWithValue }) => {
        try {
            const response = await proposalGateway.get(`${serviceEndpoints.videoManagement}/refresh-status/${id}`);
            const { success, message } = response.data;
            if (success) {
                const status = response.data?.data?.status;
                const notificationType = status === 1 || status === 2 ? 'warning' : 'success';
                let toastMessage = message || 'Status updated!';
                if (status === 1) toastMessage = 'Video is pending';
                if (status === 2) toastMessage = 'Video is processing';
                Notifications(toastMessage, notificationType);
                return response.data;
            }
            return rejectWithValue(message);
        } catch (error) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

// ─── 10. Get Video Thumbnail ──────────────────────────────────────────────────
export const getVideoThumbnail = createAsyncThunk(
    'video-management/getThumbnail',
    async ({ id, w = 300, h = 180 }, { rejectWithValue }) => {
        try {
            const response = await proposalGateway.get(`${serviceEndpoints.videoThumbnail}/${id}?w=${w}&h=${h}`, {
                responseType: 'blob',
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

// ─── 10. Get HeyGen Credits ──────────────────────────────────────────────────
export const getHeygenCredits = createAsyncThunk('video-management/credits', async (_, { rejectWithValue }) => {
    try {
        const response = await proposalGateway.get(`${serviceEndpoints.videoManagement}/credits`);
        return response?.data;
    } catch (error) {
        return rejectWithValue(error?.response?.data?.message || error.message);
    }
});

// ─── 11. Get HeyGen Credentials ──────────────────────────────────────────────
export const getHeygenCredentials = createAsyncThunk(
    'videoManagement/getCredentials',
    async (_, { rejectWithValue }) => {
        try {
            const response = await proposalGateway.get(serviceEndpoints.heygenCredentials);
            const { success, message } = response.data;
            if (success) return response.data;
            return rejectWithValue(message);
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// ─── 11. Update HeyGen Credentials ──────────────────────────────────────────
export const updateHeygenCredentials = createAsyncThunk(
    'videoManagement/updateCredentials',
    async (data, { rejectWithValue }) => {
        try {
            const response = await proposalGateway.put(serviceEndpoints.heygenCredentials, data);
            const { success, message } = response.data;
            if (success) {
                Notifications(message || 'Credentials updated successfully!', 'success');
                return response.data;
            }
            return rejectWithValue(message);
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// ─── 12. Verify HeyGen Connection ───────────────────────────────────────────
export const verifyHeygenConnection = createAsyncThunk(
    'videoManagement/verifyConnection',
    async (_, { rejectWithValue }) => {
        try {
            const response = await proposalGateway.get(`${serviceEndpoints.videoManagement}/credits`);
            const { success, message } = response.data;
            if (success) {
                Notifications('AI module is connected with HeyGen and is in sync', 'success');
                return response.data;
            }
            Notifications('Not connected to HeyGen. Please check your settings.', 'error');
            return rejectWithValue(message);
        } catch (error) {
            Notifications('Not connected to HeyGen. Please verify your credentials.', 'error');
            return rejectWithValue(error.message);
        }
    }
);

export const getFriendlyErrorMsg = (payload) => {
    const raw = (typeof payload === 'string' ? payload : payload?.message) || '';
    if (
        raw.toLowerCase().includes('status code 400') ||
        raw.toLowerCase().includes('balance') ||
        raw.toLowerCase().includes('credit') ||
        raw.toLowerCase().includes('quota') ||
        raw === '' ||
        raw.toLowerCase().includes('request failed')
    ) {
        return 'HeyGen quota is completed. Please top up your account to continue generating videos.';
    }
    return raw || 'Failed to generate video';
};

// ── Slice ─────────────────────────────────────────────────────────────────────
const videoManagementSlice = createSlice({
    name: 'videoManagement',
    initialState: {
        // List
        isListLoading: false,
        videoList: {},

        // Avatars
        isAvatarLoading: false,
        avatarList: [],

        // Voices
        isVoiceLoading: false,
        voiceList: [],

        // Generate
        isGenerating: false,
        generateResponse: null,

        // Video Status (polling)
        isStatusLoading: false,
        videoStatus: null,

        // Video Detail (for view modal)
        isVideoDetailLoading: false,
        videoDetail: null,

        // Edit
        isEditLoading: false,

        // Delete
        isDeleteLoading: false,

        // Download
        isDownloading: false,
        downloadUrl: null,

        // Credentials
        isCredentialsLoading: false,
        credentials: null,
        isCredentialsSaving: false,
        // Verification
        isVerifying: false,
        // Credits
        isCreditsLoading: false,
        credits: null,

        // Refresh
        isRefreshing: false,
        refreshingId: null,

        errorMessage: null,
        successMessage: null,
    },
    reducers: {
        resetMessages: (state) => {
            state.errorMessage = '';
            state.successMessage = '';
        },
    },

    extraReducers: (builder) => {
        builder
            // ── List ──────────────────────────────────────────────────────────
            .addCase(getVideoList.pending, (state) => {
                state.isListLoading = true;
                state.videoList = {};
            })
            .addCase(getVideoList.fulfilled, (state, { payload }) => {
                state.isListLoading = false;
                state.videoList = payload;
            })
            .addCase(getVideoList.rejected, (state, { payload }) => {
                state.isListLoading = false;
                state.videoList = {};
                state.errorMessage = payload || '';
            })

            // ── Avatars ───────────────────────────────────────────────────────
            .addCase(getAvatarList.pending, (state) => {
                state.isAvatarLoading = true;
                state.avatarList = [];
            })
            .addCase(getAvatarList.fulfilled, (state, { payload }) => {
                state.isAvatarLoading = false;
                // Map numeric keys to an array
                const list = payload
                    ? Object.keys(payload)
                          .filter((k) => !isNaN(k))
                          .map((k) => payload[k])
                    : [];
                state.avatarList = list;
            })
            .addCase(getAvatarList.rejected, (state, { payload }) => {
                state.isAvatarLoading = false;
                state.avatarList = [];
                state.errorMessage = payload || '';
            })

            // ── Voices ────────────────────────────────────────────────────────
            .addCase(getVoiceList.pending, (state) => {
                state.isVoiceLoading = true;
                state.voiceList = [];
            })
            .addCase(getVoiceList.fulfilled, (state, { payload }) => {
                state.isVoiceLoading = false;
                // Map numeric keys to an array
                const list = payload
                    ? Object.keys(payload)
                          .filter((k) => !isNaN(k))
                          .map((k) => payload[k])
                    : [];
                state.voiceList = list;
            })
            .addCase(getVoiceList.rejected, (state, { payload }) => {
                state.isVoiceLoading = false;
                state.voiceList = [];
                state.errorMessage = payload || '';
            })

            // ── Generate ──────────────────────────────────────────────────────
            .addCase(generateVideo.pending, (state) => {
                state.isGenerating = true;
                state.generateResponse = null;
                state.errorMessage = '';
                state.successMessage = '';
            })
            .addCase(generateVideo.fulfilled, (state, { payload }) => {
                state.isGenerating = false;
                state.generateResponse = payload;
                state.successMessage = payload?.message || '';
            })
            .addCase(generateVideo.rejected, (state, { payload }) => {
                state.isGenerating = false;
                state.generateResponse = null;
                const msg = getFriendlyErrorMsg(payload);
                state.errorMessage = msg;
                Notifications(msg, 'error');
            })

            // ── Video Status ──────────────────────────────────────────────────
            .addCase(getVideoStatus.pending, (state) => {
                state.isStatusLoading = true;
            })
            .addCase(getVideoStatus.fulfilled, (state, { payload }) => {
                state.isStatusLoading = false;
                state.videoStatus = payload?.data || null;
            })
            .addCase(getVideoStatus.rejected, (state) => {
                state.isStatusLoading = false;
                state.videoStatus = null;
            })

            // ── Video By ID ───────────────────────────────────────────────────
            .addCase(getVideoById.pending, (state) => {
                state.isVideoDetailLoading = true;
                state.videoDetail = null;
            })
            .addCase(getVideoById.fulfilled, (state, { payload }) => {
                state.isVideoDetailLoading = false;
                state.videoDetail = payload || null;
            })
            .addCase(getVideoById.rejected, (state) => {
                state.isVideoDetailLoading = false;
                state.videoDetail = null;
            })

            // ── Edit ──────────────────────────────────────────────────────────
            .addCase(editVideo.pending, (state) => {
                state.isEditLoading = true;
                state.errorMessage = '';
                state.successMessage = '';
            })
            .addCase(editVideo.fulfilled, (state, { payload }) => {
                state.isEditLoading = false;
                state.successMessage = payload?.message || '';
            })
            .addCase(editVideo.rejected, (state, { payload }) => {
                state.isEditLoading = false;
                const msg = getFriendlyErrorMsg(payload);
                state.errorMessage = msg;
                Notifications(msg, 'error');
            })

            // ── Delete ────────────────────────────────────────────────────────
            .addCase(deleteVideo.pending, (state) => {
                state.isDeleteLoading = true;
                state.errorMessage = '';
                state.successMessage = '';
            })
            .addCase(deleteVideo.fulfilled, (state, { payload }) => {
                state.isDeleteLoading = false;
                state.successMessage = payload?.message || '';
            })
            .addCase(deleteVideo.rejected, (state, { payload }) => {
                state.isDeleteLoading = false;
                state.errorMessage =
                    (typeof payload === 'string' ? payload : payload?.message) || 'Failed to delete video';
            })

            // ── Download ──────────────────────────────────────────────────────
            .addCase(downloadVideo.pending, (state) => {
                state.isDownloading = true;
                state.downloadUrl = null;
            })
            .addCase(downloadVideo.fulfilled, (state, { payload }) => {
                state.isDownloading = false;
                state.downloadUrl = payload?.s3_url || null;
            })
            .addCase(downloadVideo.rejected, (state, { payload }) => {
                state.isDownloading = false;
                state.downloadUrl = null;
                state.errorMessage =
                    (typeof payload === 'string' ? payload : payload?.message) || 'Failed to download video';
            })

            // ── Refresh ───────────────────────────────────────────────────────
            .addCase(refreshVideoStatus.pending, (state, { meta }) => {
                state.isRefreshing = true;
                state.refreshingId = meta.arg;
            })
            .addCase(refreshVideoStatus.fulfilled, (state, { payload }) => {
                state.isRefreshing = false;
                state.refreshingId = null;

                // Update the specific row in videoList if found
                if (state.videoList?.data?.rows && payload?.data) {
                    const updatedVideo = payload.data;
                    const index = state.videoList.data.rows.findIndex((r) => r.id === updatedVideo.id);
                    if (index !== -1) {
                        state.videoList.data.rows[index] = {
                            ...state.videoList.data.rows[index],
                            ...updatedVideo,
                        };
                    }
                }
            })
            .addCase(refreshVideoStatus.rejected, (state) => {
                state.isRefreshing = false;
                state.refreshingId = null;
            })

            // ── Reset actions ─────────────────────────────────────────────────
            .addCase('videoManagement/resetMessages', (state) => {
                state.errorMessage = '';
                state.successMessage = '';
            })
            .addCase('videoManagement/resetDetail', (state) => {
                state.videoDetail = null;
                state.videoStatus = null;
                state.downloadUrl = null;
            })
            // ── Get Credentials ──────────────────────────────────────────────
            .addCase(getHeygenCredentials.pending, (state) => {
                state.isCredentialsLoading = true;
                state.credentials = null;
            })
            .addCase(getHeygenCredentials.fulfilled, (state, { payload }) => {
                state.isCredentialsLoading = false;
                // Strip api_key from Redux state — store only a presence flag to prevent
                // exposure via Redux DevTools and persistence middleware.
                const { success, message, statusCode, api_key, ...credentialFields } = payload || {};
                const hasFields = Object.keys(credentialFields).length || api_key;
                state.credentials = hasFields ? { ...credentialFields, hasApiKey: Boolean(api_key) } : null;
            })
            .addCase(getHeygenCredentials.rejected, (state) => {
                state.isCredentialsLoading = false;
                state.credentials = null;
            })

            // ── Update Credentials ───────────────────────────────────────────
            .addCase(updateHeygenCredentials.pending, (state) => {
                state.isCredentialsSaving = true;
            })
            .addCase(updateHeygenCredentials.fulfilled, (state) => {
                state.isCredentialsSaving = false;
            })
            .addCase(updateHeygenCredentials.rejected, (state) => {
                state.isCredentialsSaving = false;
            })
            .addCase(getHeygenCredits.pending, (state) => {
                state.isCreditsLoading = true;
                state.errorMessage = null;
            })
            .addCase(getHeygenCredits.fulfilled, (state, action) => {
                state.isCreditsLoading = false;
                state.credits = action.payload;
            })
            .addCase(getHeygenCredits.rejected, (state, action) => {
                state.isCreditsLoading = false;
                state.errorMessage = action.payload;
            })
            // ── Verify Connection ──────────────────────────────────────────────
            .addCase(verifyHeygenConnection.pending, (state) => {
                state.isVerifying = true;
            })
            .addCase(verifyHeygenConnection.fulfilled, (state, { payload }) => {
                state.isVerifying = false;
                state.credits = payload;
            })
            .addCase(verifyHeygenConnection.rejected, (state) => {
                state.isVerifying = false;
            });
    },
});

export default videoManagementSlice.reducer;
